import { useEffect, useRef } from "react";

const VERT = `attribute vec2 p;void main(){gl_Position=vec4(p,0.,1.);}`;

// Liquid chrome: warped noise height field, lit with a fake chrome reflection.
// OCT controls noise octaves (lighter on low-power devices).
const frag = (oct) => `precision mediump float;
#define OCT ${oct}
uniform vec2 r;uniform float t;uniform vec2 m;
float h(vec2 p){return fract(sin(dot(p,vec2(127.1,311.7)))*43758.5453);}
float n(vec2 p){vec2 i=floor(p),f=fract(p);f=f*f*(3.-2.*f);
return mix(mix(h(i),h(i+vec2(1,0)),f.x),mix(h(i+vec2(0,1)),h(i+vec2(1,1)),f.x),f.y);}
float fbm(vec2 p){float v=0.,a=.5;for(int i=0;i<OCT;i++){v+=a*n(p);p=p*2.03+vec2(1.7,9.2);a*=.5;}return v;}
void main(){
 vec2 uv=(gl_FragCoord.xy-.5*r)/r.y;
 vec2 mm=(m-.5)*vec2(r.x/r.y,1.);
 float d=length(uv-mm);
 uv+=(uv-mm)*.22*exp(-d*2.5);
 float T=t*.05;
 vec2 p=uv*.8;
 // one shared warp field, three cheap height taps (forward differences)
 vec2 q=vec2(fbm(p*.9+vec2(T,-T*.6)),fbm(p*.9+vec2(3.1-T*.7,1.3+T)));
 vec2 b=p*.7+q*1.9;
 float e=.02;
 float hc=fbm(b),hx=fbm(b+vec2(e,0.)),hy=fbm(b+vec2(0.,e));
 vec3 nr=normalize(vec3(hc-hx,hc-hy,e*1.1));
 vec3 rf=reflect(vec3(0.,0.,-1.),nr);
 float env=.5+.5*sin(rf.x*5.+rf.y*3.+hc*9.);
 float spec=pow(max(dot(rf,normalize(vec3(.4+mm.x*.5,.6+mm.y*.5,.7))),0.),24.);
 float rim=pow(1.-nr.z,2.);
 vec3 lav=vec3(.718,.58,.965);
 vec3 col=vec3(.03,.028,.04)+lav*env*env*env*.55*rim*3.+lav*.15*env;
 col+=vec3(.97,.95,1.)*spec*1.1;
 col*=smoothstep(.15,.75,hc+.2);
 col*=smoothstep(1.3,.25,length(uv*vec2(.75,1.)-vec2(.25,0.)));
 col=mix(col,vec3(.039),smoothstep(.1,-.95,uv.x+.15)*.75);
 gl_FragColor=vec4(col,1.);
}`;

const isLowPower = () =>
  window.matchMedia("(pointer: coarse)").matches ||
  (navigator.hardwareConcurrency || 8) <= 4 ||
  (navigator.deviceMemory || 8) <= 4;

/** Full-bleed WebGL "liquid chrome" that follows the cursor. Pauses off-screen / in hidden tabs. */
export const LiquidCanvas = ({ className = "" }) => {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    const gl = canvas.getContext("webgl", { antialias: false, alpha: false, depth: false, stencil: false, powerPreference: "high-performance" });
    if (!gl) return undefined;
    const low = isLowPower();
    const sh = (type, src) => {
      const s = gl.createShader(type);
      gl.shaderSource(s, src);
      gl.compileShader(s);
      return s;
    };
    const prog = gl.createProgram();
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, frag(low ? 3 : 4)));
    gl.linkProgram(prog);
    gl.useProgram(prog);
    gl.bindBuffer(gl.ARRAY_BUFFER, gl.createBuffer());
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);
    const uR = gl.getUniformLocation(prog, "r");
    const uT = gl.getUniformLocation(prog, "t");
    const uM = gl.getUniformLocation(prog, "m");

    // Render at reduced resolution; CSS scales it up (the look is soft anyway).
    const scale = low ? 0.33 : 0.5;
    const resize = () => {
      canvas.width = Math.max(1, Math.round(canvas.clientWidth * scale));
      canvas.height = Math.max(1, Math.round(canvas.clientHeight * scale));
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uR, canvas.width, canvas.height);
    };
    resize();

    const mouse = { x: 0.68, y: 0.5, tx: 0.68, ty: 0.5 };
    let rect = canvas.getBoundingClientRect();
    const onMove = (e) => {
      mouse.tx = (e.clientX - rect.left) / rect.width;
      mouse.ty = 1 - (e.clientY - rect.top) / rect.height;
    };
    const onResize = () => { resize(); rect = canvas.getBoundingClientRect(); };
    const onScroll = () => { rect = canvas.getBoundingClientRect(); };
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("pointermove", onMove, { passive: true });

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    let raf = 0;
    let inView = true;
    let elapsed = 20;
    let last = 0;
    const frame = (now) => {
      raf = requestAnimationFrame(frame);
      const dt = last ? Math.min((now - last) / 1000, 0.05) : 0;
      last = now;
      if (!reduce) elapsed += dt;
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      gl.uniform1f(uT, elapsed);
      gl.uniform2f(uM, mouse.x, mouse.y);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };
    const run = () => {
      if (!raf && inView && !document.hidden) { last = 0; raf = requestAnimationFrame(frame); }
    };
    const stop = () => { cancelAnimationFrame(raf); raf = 0; };
    const sync = () => (inView && !document.hidden ? run() : stop());
    const io = new IntersectionObserver(([e]) => { inView = e.isIntersecting; sync(); });
    io.observe(canvas);
    document.addEventListener("visibilitychange", sync);
    run();
    return () => {
      stop();
      io.disconnect();
      document.removeEventListener("visibilitychange", sync);
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("pointermove", onMove);
    };
  }, []);

  return (
    <canvas
      ref={ref}
      aria-hidden="true"
      className={`h-full w-full bg-[radial-gradient(circle_at_70%_50%,rgba(183,148,246,0.35),transparent_55%)] ${className}`}
    />
  );
};
